import { useCallback, useEffect, useRef, useState } from 'react';
import { TEXTURES } from '@/lib/assets';
import { itemImage } from '@/lib/assets';
import { formatNumber } from '@/lib/utils';
import type { QuestRewards } from '@/types/quests';

type Phase = 'ready' | 'attempting' | 'success';

interface ActionModalProps {
  description: string;
  buttonText: string;
  successText: string;
  rewards?: QuestRewards;
  durationMs?: number;
  onComplete: () => void;
  onClose: () => void;
}

export function ActionModal({
  description,
  buttonText,
  successText,
  rewards,
  durationMs = 2000,
  onComplete,
  onClose,
}: ActionModalProps): JSX.Element {
  const [phase, setPhase] = useState<Phase>('ready');
  const dialogRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLButtonElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase === 'ready') onClose();
    },
    [onClose, phase],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prev;
    };
  }, [handleKeyDown]);

  useEffect(() => {
    const timer = setTimeout(() => actionRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [phase]);

  function handleAttempt(): void {
    setPhase('attempting');
    setTimeout(() => setPhase('success'), durationMs);
  }

  const paragraphs = description.split('\n\n').filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-3 py-6 sm:px-6"
      role="dialog"
      aria-modal="true"
      aria-label="Action"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      <div
        ref={dialogRef}
        className="relative w-full max-w-xl rounded-lg border-2 border-wood-mid/70 shadow-[0_8px_40px_rgba(0,0,0,0.7)] stage-fade-in"
        style={{
          backgroundImage: `url("${TEXTURES.parchment}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-parchment/85 pointer-events-none rounded-lg" />

        <div className="relative px-5 sm:px-8 pt-8 pb-6">
          {phase === 'ready' && (
            <>
              <div className="space-y-3 text-ink text-base sm:text-lg leading-relaxed mb-8 italic">
                {paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <button
                ref={actionRef}
                type="button"
                onClick={handleAttempt}
                className="w-full inline-flex items-center justify-center rounded-md bg-sea-deep text-candle border-2 border-candle/50 px-5 py-3 font-display tracking-[0.2em] uppercase text-sm shadow-[0_2px_8px_rgba(0,0,0,0.4)] hover:bg-sea-dark hover:border-candle hover:shadow-[0_2px_12px_rgba(232,177,75,0.2)] transition-all duration-200 min-h-12 focus:outline-none focus-visible:ring-2 focus-visible:ring-candle"
              >
                {buttonText}
              </button>
            </>
          )}

          {phase === 'attempting' && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center gap-3 text-candle/60 mb-4">
                <span aria-hidden className="h-px w-10 bg-candle/40" />
                <span
                  aria-hidden
                  className="inline-block h-3 w-3 rounded-full bg-candle animate-pulse"
                />
                <span aria-hidden className="h-px w-10 bg-candle/40" />
              </div>
              <p
                className="font-display text-lg sm:text-xl text-ink tracking-wider animate-pulse"
                role="status"
              >
                Steady hands&hellip;
              </p>
            </div>
          )}

          {phase === 'success' && (
            <div className="stage-fade-in">
              <p className="text-ink text-base sm:text-lg leading-relaxed mb-6 italic">
                {successText}
              </p>

              {rewards && (
                <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
                  {rewards.doubloons && (
                    <div className="flex items-center gap-2 rounded-lg border-2 border-candle/40 bg-candle/10 px-4 py-2.5 shadow-sm stage-fade-in">
                      <img
                        src={itemImage('doubloon')}
                        alt="Doubloon"
                        className="h-8 w-8 object-contain"
                        width={32}
                        height={32}
                      />
                      <span className="font-display text-xl text-candle-dark">
                        +{formatNumber(rewards.doubloons)}
                      </span>
                    </div>
                  )}
                  {rewards.xp && (
                    <div className="flex items-center gap-2 rounded-lg border-2 border-sea-light/40 bg-sea-light/10 px-4 py-2.5 shadow-sm stage-fade-in">
                      <span className="font-display text-xl text-sea-light">
                        +{formatNumber(rewards.xp)} XP
                      </span>
                    </div>
                  )}
                </div>
              )}

              <button
                ref={actionRef}
                type="button"
                onClick={onComplete}
                className="w-full inline-flex items-center justify-center rounded-md bg-sea-deep text-candle border-2 border-candle/50 px-5 py-3 font-display tracking-[0.2em] uppercase text-sm shadow-[0_2px_8px_rgba(0,0,0,0.4)] hover:bg-sea-dark hover:border-candle transition-all duration-200 min-h-12 focus:outline-none focus-visible:ring-2 focus-visible:ring-candle"
              >
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
